import { useCallback, useEffect, useState } from "react";
import { api, getAuth } from "./api";

/**
 * Phase 151 — Batch Log cleanup helpers (tenant side).
 *
 * `useBatchLogAccess(code)` → { canDelete, allowedForAdmin, loading, refresh }
 *   Who may remove batch history records on this municipality's pages: the platform Super Admin always,
 *   the Municipal Admin only once the Super Admin switched it on (Super Admin → Batch Logs).
 *   Cached per municipality for the session so every list on the page shares one request.
 *
 * `deleteBatchLogs(code, items)` → POST /t/{code}/batch-logs/delete  (items = [{kind, id}])
 *   Soft-deletes the LOG ENTRIES ONLY. Imported properties, demands, payments and receipts are never touched.
 */
const cache = new Map();          // code -> {can_delete, allowed_for_admin, role}
const listeners = new Map();      // code -> Set<fn>

const notify = (code) => (listeners.get(code) || new Set()).forEach((fn) => fn(cache.get(code)));

export const fetchBatchLogAccess = async (code, { force = false } = {}) => {
  if (!force && cache.has(code)) return cache.get(code);
  const role = getAuth()?.user?.role;
  if (role && role !== "municipal_admin" && role !== "super_admin") {
    const denied = { can_delete: false, allowed_for_admin: false, role };
    cache.set(code, denied);
    return denied;
  }
  try {
    const r = await api.get(`/t/${code}/batch-logs/access`);
    cache.set(code, r.data || { can_delete: false });
  } catch {
    cache.set(code, { can_delete: false, allowed_for_admin: false, role, error: true });
  }
  notify(code);
  return cache.get(code);
};

export const useBatchLogAccess = (code) => {
  const [state, setState] = useState(() => cache.get(code) || null);

  useEffect(() => {
    if (!code) return undefined;
    const set = listeners.get(code) || new Set();
    set.add(setState);
    listeners.set(code, set);
    if (cache.has(code)) setState(cache.get(code));
    else fetchBatchLogAccess(code).then(setState);
    return () => { set.delete(setState); };
  }, [code]);

  const refresh = useCallback(() => fetchBatchLogAccess(code, { force: true }).then(setState), [code]);
  return { canDelete: !!state?.can_delete, allowedForAdmin: !!state?.allowed_for_admin, role: state?.role,
    loading: state === null, refresh };
};

export const deleteBatchLogs = async (code, items) => {
  const r = await api.post(`/t/${code}/batch-logs/delete`, { items });
  return r.data;
};

export const KIND_LABELS = {
  excel: "Bulk Excel Upload", bulk_entry: "Bulk Entry", bulk_update: "Bulk Update", payment_import: "Import Payments",
};

/** Small helper for the row checkboxes: a stable `${kind}:${id}` key and back. */
export const logKey = (kind, id) => `${kind}:${id}`;
export const parseLogKey = (key) => { const i = key.indexOf(":"); return { kind: key.slice(0, i), id: key.slice(i + 1) }; };
